import React, { useEffect, useMemo, useState } from "react"
import { useAuth } from "../context/AuthContext"
import { api } from "../api/client"
import LoadingSpinner from "../components/LoadingSpinner"

export default function AdminUsers() {
  const { user } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")

  useEffect(() => {
    if (!user?.is_admin) {
      setLoading(false)
      return
    }

    const fetchUsers = async () => {
      try {
        setLoading(true)
        setError("")
        const data = await api.adminUsers()
        setUsers(Array.isArray(data) ? data : [])
      } catch (err) {
        setError(err.message || "Unable to load users")
      } finally {
        setLoading(false)
      }
    }

    fetchUsers()
  }, [user])

  const filteredUsers = useMemo(() => {
    let filtered = users

    if (statusFilter !== "all") {
      filtered = filtered.filter((u) =>
        statusFilter === "active" ? u.is_active : !u.is_active
      )
    }

    if (search.trim()) {
      const term = search.toLowerCase()
      filtered = filtered.filter(
        (u) =>
          u.email?.toLowerCase().includes(term) ||
          u.username?.toLowerCase().includes(term) ||
          `${u.first_name || ""} ${u.last_name || ""}`
            .toLowerCase()
            .includes(term)
      )
    }

    return filtered
  }, [users, search, statusFilter])

  const stats = useMemo(() => {
    const total = users.length
    const active = users.filter((u) => u.is_active).length
    const admins = users.filter((u) => u.is_admin).length
    return {
      total,
      active,
      inactive: total - active,
      admins,
    }
  }, [users])

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M10 9V5a2 2 0 114 0v4m-7 4h10m-8 0v4m-2-4v4m10-4v4"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-foreground">Admin access required</h1>
          <p className="text-muted-foreground">
            You need administrator privileges to view this area. Please contact support if you believe this is an error.
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <LoadingSpinner message="Loading users..." />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div className="space-y-2">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
            Admin Tools
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">User Directory</h1>
          <p className="text-muted-foreground max-w-2xl">
            View and audit all registered users. New submissions and account changes appear here instantly.
          </p>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive rounded-2xl p-4">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Users", value: stats.total },
            { label: "Active", value: stats.active },
            { label: "Inactive", value: stats.inactive },
            { label: "Admins", value: stats.admins },
          ].map((stat) => (
            <div key={stat.label} className="bg-card/60 border border-border rounded-2xl p-4 shadow-sm">
              <p className="text-sm text-muted-foreground">{stat.label}</p>
              <div className="text-3xl font-semibold text-foreground mt-2">{stat.value}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between bg-card/60 border border-border rounded-2xl p-4">
          <div className="relative w-full md:w-1/3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <span className="absolute inset-y-0 left-3 flex items-center text-muted-foreground">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1117.5 10a7.5 7.5 0 01-.85 3.15z"
                />
              </svg>
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Status:</span>
            {["all", "active", "inactive"].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  statusFilter === status
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card/60 border border-border rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50">
                <tr>
                  {["User", "Email", "Role", "Status", "Joined"].map((header) => (
                    <th key={header} className="px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr key={u.id} className="border-t border-border/50 hover:bg-muted/30">
                      <td className="px-6 py-4">
                        <div className="font-medium text-foreground">
                          {u.first_name || u.username || "Unnamed"} {u.last_name || ""}
                        </div>
                        <div className="text-xs text-muted-foreground">ID: {u.id}</div>
                      </td>
                      <td className="px-6 py-4 text-foreground">{u.email}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            u.is_admin ? "bg-purple-100 text-purple-700" : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {u.is_admin ? "Admin" : "User"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            u.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          }`}
                        >
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}